"use client";

import { c as _c } from "react/compiler-runtime";
import { useRef, useState } from "react";
import { commit, redo, startHistory, undo } from "@/lib/editor/history";
import { newId } from "@/lib/editor/items";
import type { Item, NewItem } from "@/lib/pdf/items";

/** Items added to a PDF, with undo/redo and the current selection (FR-13). */
export function useEditor() {
  const $ = _c(10);
  const [history, setHistory] = useState(_temp);
  const [selectedId, setSelectedId] = useState(null);
  const coalescing = useRef(null);
  const items = history.present;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = function change(next, t1) {
      const coalesceKey = t1 === undefined ? null : t1;
      setHistory(h => coalesceKey && coalescing.current === coalesceKey ? {
        ...h,
        present: next
      } : commit(h, next));
      coalescing.current = coalesceKey;
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const change = t0;
  let t1;
  if ($[1] !== items || $[2] !== selectedId) {
    t1 = items.find(i => i.id === selectedId) ?? null;
    $[1] = items;
    $[2] = selectedId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== history.future.length || $[5] !== history.past.length || $[6] !== items || $[7] !== selectedId || $[8] !== t1) {
    t2 = {
      items,
      selectedId,
      selected: t1,
      select(id) {
        coalescing.current = null;
        setSelectedId(id);
      },
      add(item) {
        const withId = {
          ...item,
          id: newId()
        } as Item;
        change([...items, withId]);
        setSelectedId(withId.id);
        return withId;
      },
      update(next_0, typing) {
        change(items.map(i_0 => i_0.id === next_0.id ? next_0 : i_0), typing ? `${next_0.id}:${typing}` : null);
      },
      remove(id_0) {
        change(items.filter(i_1 => i_1.id !== id_0));
        if (selectedId === id_0) {
          setSelectedId(null);
        }
      },
      undo() {
        coalescing.current = null;
        setHistory(undo);
        setSelectedId(null);
      },
      redo() {
        coalescing.current = null;
        setHistory(redo);
        setSelectedId(null);
      },
      canUndo: history.past.length > 0,
      canRedo: history.future.length > 0,
      reset() {
        setHistory(startHistory([]));
        setSelectedId(null);
      }
    };
    $[4] = history.future.length;
    $[5] = history.past.length;
    $[6] = items;
    $[7] = selectedId;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
function _temp() {
  return startHistory([]);
}
export type Editor = ReturnType<typeof useEditor>;